// DuckDbDataTable — a generic server-side table: a card with a title, a debounced search field and
// a `v-data-table-server` whose every page comes from the app's `fetchPage`. Desktop and mobile
// layouts (below Vuetify's `md`), each with its own toolbar slot. Schema-agnostic by rule: no
// project SQL, no table names, no store, no router — rows, counts, sorting and searching are the
// app's, through the function prop (PLATFORM.md §3.2 rules 1–3). Born at IA_ABSORPTION_PLAN
// Phase 4 step 13.3a from issue-analysis's `components/grids/base/BaseGrid.vue`.
//
//   props:  title               (String)        rendered as-is; already translated
//           headers             (Array)         Vuetify headers; a `titleKey` (raw app key) is
//                                               translated, else `title` is shown as-is
//           fetchPage           (Function)      ({ page, itemsPerPage, sort: { key?, order? }, search })
//                                               => Promise<{ items, totalItems }>; only the LATEST
//                                               call's result is applied
//           itemValue           (String)        the row key (Vuetify's item-value)
//           defaultSort         (Array)         the initial sortBy, [{ key, order }]
//           itemsPerPageOptions (Array)         the page-size menu
//           showSearch          (Boolean)       the search field
//           searchDebounce      (Number)        ms between the last keystroke and the reload
//           filterContext       (Object|Array)  the filter-context duck type, or an array of them:
//                                               a tick from any reloads from page 1
//           dataLoaded          (Boolean)       turning true reloads from page 1 (it gates nothing)
//           reloadKey           (any)           a change reloads from page 1, like a tick
//   attrs:  inheritAttrs: false — every other attribute goes to BOTH v-data-table-servers
//           (must-sort, multi-sort, density, …: rule 12, no re-declared Vuetify props)
//   slots:  toolbar-actions / mobile-toolbar-actions  { items, search } — after the search field
//           desktop-row       { item, columns }  the whole desktop row (default: one cell per column)
//           item.<key>        { item, value }    one cell of the default desktop row
//           mobile-item       { item }           the mobile card (default: the row as JSON)
//           any other slot is passed through to the desktop v-data-table-server
//   emits:  itemsLoaded { page, itemsPerPage, sort, search, items, totalItems } — each applied page
//           error       the error a `fetchPage` threw (the table then shows no rows and 0 items)
//   exposed: reload()   back to page 1 and reload now (footer-safe); items — the rows on screen
//
// The page, page size and sort are v-model'd on the server tables, so the footer can never stay on
// one page while the rows come from another: every reload from page 1 MOVES the table's page.
//
// Full contract: ../../../README.md. The logic lives in ./setup.js.
import setup from './setup.js';

export default {
  name: 'duck-db-data-table',
  inheritAttrs: false,
  props: {
    // Rendered as-is — already translated by the app
    title: {
      type: String,
      default: ''
    },
    // Vuetify data-table headers: [{ key, title | titleKey, align?, sortable?, width? }]
    headers: {
      type: Array,
      required: true
    },
    // ({ page, itemsPerPage, sort: { key?, order? }, search }) => Promise<{ items, totalItems }>
    fetchPage: {
      type: Function,
      required: true
    },
    itemValue: {
      type: String,
      default: 'id'
    },
    // The initial sort, [{ key, order }]; [] = the provider's own default order
    defaultSort: {
      type: Array,
      default: () => []
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [
        { title: '10', value: 10 },
        { title: '50', value: 50 },
        { title: '100', value: 100 },
        { title: '150', value: 150 },
        { title: '200', value: 200 }
      ]
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    // Read once, at setup
    searchDebounce: {
      type: Number,
      default: 500
    },
    // One filter-context duck type, or an ARRAY of them: a tick from any reloads from page 1
    filterContext: {
      type: [Object, Array],
      default: null
    },
    // false → true reloads from page 1; it does NOT gate a load
    dataLoaded: {
      type: Boolean,
      default: false
    },
    // Any value; a change reloads from page 1 (app state the provider reads that is not a filter)
    reloadKey: {
      type: null,
      default: null
    }
  },
  emits: ['itemsLoaded', 'error'],
  setup,
};
